/* import __COLOCATED_TEMPLATE__ from './add-mcp-server-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { post } from 'embercom/lib/ajax';
import type IntlService from 'embercom/services/intl';
import type { Template } from './mcp-template-list';
import type Store from '@ember-data/store';
import type Token from 'embercom/models/custom-authentication/token';
import { taskFor } from 'ember-concurrency-ts';
import {
  MCP_SERVER_AUTHENTICATION_TYPES,
  SHOPIFY_URL_PLACEHOLDER,
  type McpAuthenticationType,
} from 'embercom/models/workflow-connector/constants/mcp-templates';
import { action } from '@ember/object';

interface Args {
  onModalClose: () => void;
  selectedTemplate: Template;
}

type AuthenticationOption = {
  value: McpAuthenticationType | 'existing_token';
  text: string;
};

export default class AddMcpServerModal extends Component<Args> {
  @service declare appService: $TSFixMe;
  @service declare router: $TSFixMe;
  @service declare redirectService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare intercomEventService: any;
  @service declare notificationsService: any;

  @tracked serverName = '';
  @tracked serverUrl = '';
  @tracked serverDescription = '';
  @tracked tokenInputValue = '';
  @tracked customAuthenticationTokens: Token[] | undefined;
  @tracked selectedTokenId: string | undefined;
  @tracked selectedAuthenticationType: McpAuthenticationType | 'existing_token' =
    MCP_SERVER_AUTHENTICATION_TYPES.token;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (this.args.selectedTemplate.template) {
      this.serverName = this.args.selectedTemplate.name;
    }
    taskFor(this.loadCustomAuthenticationTokens).perform();
  }

  @task({ drop: true })
  *loadCustomAuthenticationTokens(): TaskGenerator<void> {
    this.customAuthenticationTokens = yield this.store.findAll('custom-authentication/token');
  }

  get authenticationOptions(): AuthenticationOption[] {
    return [
      {
        value: MCP_SERVER_AUTHENTICATION_TYPES.none,
        text: this.intl.t('workflow-connector.actions.mcp-actions.add-mcp-server-form.auth-none'),
      },
      {
        value: MCP_SERVER_AUTHENTICATION_TYPES.token,
        text: this.intl.t('workflow-connector.actions.mcp-actions.add-mcp-server-form.auth-token'),
      },
      {
        value: 'existing_token',
        text: this.intl.t(
          'workflow-connector.actions.mcp-actions.add-mcp-server-form.auth-existing-token',
        ),
      },
      {
        value: MCP_SERVER_AUTHENTICATION_TYPES.oauth,
        text: this.intl.t('workflow-connector.actions.mcp-actions.add-mcp-server-form.auth-oauth'),
      },
    ];
  }

  get tokenList() {
    return (
      this.customAuthenticationTokens?.map((token) => ({
        value: token.id,
        text: token.name,
      })) || []
    );
  }

  @action
  updateAuthenticationType(authType: McpAuthenticationType | 'existing_token') {
    this.selectedAuthenticationType = authType;
    this.selectedTokenId = undefined;
    this.tokenInputValue = '';
  }

  @action
  updateSelectedToken(tokenId: string) {
    this.selectedTokenId = tokenId;
  }

  @action
  updateTokenInputValue(event: Event) {
    let target = event.target as HTMLInputElement;
    this.tokenInputValue = target.value;
  }

  get isTemplateServer() {
    return this.args.selectedTemplate.template;
  }

  get modalTitle() {
    return this.isTemplateServer
      ? this.args.selectedTemplate.name
      : this.intl.t('workflow-connector.actions.mcp-actions.template-list.custom-mcp-title');
  }

  get submitButtonLabel() {
    return this.isTemplateServer
      ? this.intl.t(
          'workflow-connector.actions.mcp-actions.add-mcp-server-form.add-server-template-button',
          { name: this.args.selectedTemplate.name },
        )
      : this.intl.t(
          'workflow-connector.actions.mcp-actions.add-mcp-server-form.add-custom-server-button',
        );
  }

  get buildServerUrl() {
    if (this.isTemplateServer && this.args.selectedTemplate.name === 'Shopify') {
      return `${this.serverUrl}/api/mcp`;
    }

    return this.args.selectedTemplate.url;
  }

  get shopifyUrlPlaceholder() {
    return SHOPIFY_URL_PLACEHOLDER;
  }

  get resolvedAuthFields() {
    if (this.isTemplateServer) {
      let authType = this.args.selectedTemplate.authentication;

      if (authType === MCP_SERVER_AUTHENTICATION_TYPES.token && this.selectedTokenId) {
        return {
          auth_type: MCP_SERVER_AUTHENTICATION_TYPES.token,
          token_id: this.selectedTokenId,
        };
      }

      return { auth_type: authType };
    } else {
      if (this.selectedAuthenticationType === 'existing_token') {
        return {
          auth_type: MCP_SERVER_AUTHENTICATION_TYPES.token,
          token_id: this.selectedTokenId,
        };
      } else if (this.selectedAuthenticationType === MCP_SERVER_AUTHENTICATION_TYPES.token) {
        return {
          auth_type: MCP_SERVER_AUTHENTICATION_TYPES.token,
          token_value: this.tokenInputValue,
        };
      }

      return { auth_type: this.selectedAuthenticationType };
    }
  }

  get authenticationUrl() {
    return this.router.urlFor('apps.app.settings.app-settings.authentication');
  }

  trackAction(actionName: string, authType: McpAuthenticationType | 'existing_token') {
    this.intercomEventService.trackAnalyticsEvent({
      action: actionName,
      object: 'mcp_server',
      auth_type: authType,
    });
  }

  *handleOAuthMcpServerCreation(): TaskGenerator<void> {
    let response = yield post('/ember/workflow_connector/mcp_servers', {
      app_id: this.appService.app.id,
      name: this.isTemplateServer ? this.args.selectedTemplate.name : this.serverName,
      url: this.isTemplateServer ? this.buildServerUrl : this.serverUrl,
      description: this.isTemplateServer ? this.args.selectedTemplate.description : null,
      auth_type: MCP_SERVER_AUTHENTICATION_TYPES.oauth,
    });

    if (response.authorization_url) {
      this.trackAction('mcp_server_creation_success', MCP_SERVER_AUTHENTICATION_TYPES.oauth);
      this.redirectService.redirect(response.authorization_url);
    }
  }

  *handleMcpServerCreation(): TaskGenerator<void> {
    let serverData: any = {
      name: this.isTemplateServer ? this.args.selectedTemplate.name : this.serverName,
      url: this.isTemplateServer ? this.buildServerUrl : this.serverUrl,
      description: this.isTemplateServer ? this.args.selectedTemplate.description : null,
      ...this.resolvedAuthFields,
    };

    let mcpServer = this.store.createRecord('workflow-connector/mcp-server', serverData);
    try {
      yield mcpServer.save();
      this.trackAction('mcp_server_creation_success', MCP_SERVER_AUTHENTICATION_TYPES.token);
    } catch (error) {
      mcpServer.rollbackAttributes();
      this.store.unloadRecord(mcpServer);
      throw error;
    }
  }

  @task({ drop: true })
  *addMcpServer(): TaskGenerator<void> {
    let authType = this.isTemplateServer
      ? this.args.selectedTemplate.authentication
      : this.selectedAuthenticationType;

    if (authType) {
      try {
        this.trackAction('mcp_server_creation_attempt', authType);
        authType === MCP_SERVER_AUTHENTICATION_TYPES.oauth
          ? yield* this.handleOAuthMcpServerCreation()
          : yield* this.handleMcpServerCreation();
      } catch (error: any) {
        this.trackAction('mcp_server_creation_failed', authType);
        console.error('Error adding MCP server:', error);

        if (error?.jqXHR?.status >= 400 && error?.jqXHR?.status < 500) {
          this.notificationsService.notifyError(
            this.intl.t('workflow-connector.actions.mcp-actions.add-mcp-server-client-error'),
          );
        } else {
          this.notificationsService.notifyError(
            this.intl.t('workflow-connector.actions.mcp-actions.add-mcp-server-error'),
          );
        }
      } finally {
        this.args.onModalClose();
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::AddMcpServerModal': typeof AddMcpServerModal;
    'workflow-connector/add-mcp-server-modal': typeof AddMcpServerModal;
  }
}
